import { LogPhotoStrip } from "../LogPhotoStrip";
import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Plus, Check, X, AlertTriangle, Activity, Trophy, Grid3x3, List, Columns, ChevronDown, GripVertical, Footprints, Dumbbell, Bike, Mountain, Flag, Zap, PersonStanding, Moon, MessageCircle, Pencil, Star, Send, Smile, Meh, Frown, Laugh, Angry } from "lucide-react";
import WorkoutEditor from "./WorkoutEditor";
import CoachWorkoutLogOverrideModal from "./CoachWorkoutLogOverrideModal";
import WorkoutDetail from "../athlete/WorkoutDetail";
import { RichTextEditor, RichTextView } from "../RichTextEditor";
import WorkoutCommentsThread from "../WorkoutCommentsThread";
import { coachListWorkoutComments, coachPostWorkoutComment, coachProgramAthleteExtras } from "../../lib/api";
import { toast } from "sonner";
import { WORKOUT_TYPE_MAP } from "../../lib/coaching";

// Persist the coach's last chosen view across week switches + page reloads.
// (Local-only — no need to round-trip to the server.)
const VIEW_STORAGE_KEY = "tir-weekcanvas-view";

function newWorkout() {
  return {
    id: `tmp-${Math.random().toString(36).slice(2, 10)}`,
    type: "easy",
    title: "",
    slot: "any",
    duration_or_distance: "",
    zones: [],
    rpe_min: null,
    rpe_max: null,
    structure: "",
    session_note: "",
  };
}

/**
 * Day-status dot indicator.
 * Status: matched | partial | missed | rest | unmatched_only | null (= no Strava data yet)
 */
function DayStatusDot({ status }) {
  if (!status || status === "rest" || status === "pending") return null;
  const map = {
    matched: { icon: Check, cls: "bg-[#2f7d4a] text-white border-[#2f7d4a]", title: "All workouts completed" },
    partial: {
      icon: AlertTriangle,
      cls: "bg-[#e6a35a] text-white border-[#e6a35a]",
      title: "Partially completed",
    },
    missed: { icon: X, cls: "bg-[var(--tir-burgundy)] text-white border-[var(--tir-burgundy)]", title: "Missed — no matching activity or log" },
    unmatched_only: {
      icon: Activity,
      cls: "bg-white text-[var(--tir-muted)] border-[var(--tir-border)]",
      title: "Activity logged with no prescribed workout",
    },
  };
  const s = map[status];
  if (!s) return null;
  const Icon = s.icon;
  return (
    <span
      className={`inline-flex items-center justify-center h-4 w-4 rounded-full border ${s.cls}`}
      title={s.title}
      data-testid={`day-status-${status}`}
    >
      <Icon size={9} strokeWidth={3} />
    </span>
  );
}

function formatPace(meters, seconds) {
  if (!meters || !seconds || meters < 100 || seconds < 30) return "";
  const km = meters / 1000;
  const minPerKm = seconds / 60 / km;
  const m = Math.floor(minPerKm);
  const s = Math.round((minPerKm - m) * 60);
  if (m < 2 || m > 12) return "";
  return `${m}:${String(s).padStart(2, "0")}/km`;
}

/**
 * Activity readout shown under a day's workout cards (when Strava data is available).
 */
function DayActivityList({ dayCompletion }) {
  if (!dayCompletion || !dayCompletion.activities || dayCompletion.activities.length === 0) {
    return null;
  }
  return (
    <div className="border-t border-dashed border-[var(--tir-border)] pt-1.5 mt-1.5 space-y-1">
      {dayCompletion.activities.map((a) => {
        const km = (a.distance_m || 0) / 1000;
        const mins = Math.round((a.moving_time_s || 0) / 60);
        const pace = formatPace(a.distance_m, a.moving_time_s);
        return (
          <div
            key={a.id}
            className="flex items-baseline gap-1.5 text-[10px]"
            data-testid={`strava-activity-${a.id}`}
          >
            <Activity size={9} className="text-[#fc4c02] shrink-0" />
            <span className="text-[var(--tir-ink)] truncate flex-1 leading-tight">{a.name || a.sport_type}</span>
            <span className="font-mono-tir text-[var(--tir-muted)] whitespace-nowrap">
              {km > 0 ? `${km.toFixed(1)}k` : ""}
              {km > 0 && mins > 0 ? " · " : ""}
              {mins > 0 ? `${mins}m` : ""}
              {pace ? ` · ${pace}` : ""}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Renders one week's 7 days as columns (desktop) / stacked (mobile).
 * Workouts can be dragged within a day or across days.
 *
 * Props:
 *   week: { id, week_number, phase_label, is_recovery, coach_note, days: [{ id, label, workouts }] }
 *   onChange: (updatedWeek) => void
 *   completion: optional response from /weeks/{n}/completion endpoint
 *   athletePaces: optional VDOT-derived pace ranges
 *   weekStartDate: optional ISO string of the Monday of this week (for race lookup)
 *   races: optional array of athlete race objects { id, name, date, distance, priority }
 *   logsLookup: optional map keyed by `${week_number}-${day_label}-${workout_id}` → log row
 */
export default function WeekCanvas({ week, onChange, completion, athletePaces, paceSource, weekStartDate, races, logsLookup, startDay = "mon", athleteId, programId, allWeeks, currentWeekNumber, onCrossWeekDuplicate, onLogChanged }) {
  // Visual day label for the header (e.g. "Sun" when start_day is "sun")
  // while keeping `day.label` ("Mon"/"Tue"/etc.) as the canonical data key
  // used for testids, log lookups, and backend keys. This is what fixed
  // Sheri's "Wed May 12" bug — the dates were always right, but the label
  // above each date never reflected the program's actual start_day.
  const dayLabelsForDisplay = (() => {
    if (startDay === "sun") return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    if (startDay === "day1") return ["Day 1", "Day 2", "Day 3", "Day 4", "Day 5", "Day 6", "Day 7"];
    return ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  })();
  const visualLabel = (di) => dayLabelsForDisplay[di] || `Day ${di + 1}`;
  const [editing, setEditing] = useState(null); // { dayId, workoutIndex, workout }
  // Athlete extras: workouts the athlete added themselves + day shifts —
  // fetched once here and passed to all three views. Rendered read-only so
  // coach edits never write athlete items into the master template.
  const [athleteExtras, setAthleteExtras] = useState(null);
  useEffect(() => {
    if (!programId || !athleteId) return;
    coachProgramAthleteExtras(programId).then(setAthleteExtras).catch(() => {});
  }, [programId, athleteId]);
  const additionsForDay = (label) =>
    (athleteExtras?.additions || []).filter(
      (a) => Number(a.week_number) === Number(week.week_number) && a.day_label === label
    );
  const shiftForWorkout = (id) =>
    (athleteExtras?.shifts || []).find(
      (s) => Number(s.week_number) === Number(week.week_number) && s.workout_id === id && s.to_day
    );
  const [editingLog, setEditingLog] = useState(null); // { workout, log, dayLabel }
  // "grid" (the classic 7-col week-at-a-glance) or "list" (the same
  // vertical day-row layout the athlete sees in their portal). Coaches can
  // flip between them per-week; choice is remembered locally.
  const [viewMode, setViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem(VIEW_STORAGE_KEY);
      if (saved === "list" || saved === "grid" || saved === "row") return saved;
      return "row"; // Default: Week view (7-col horizontal), per coach request.
    } catch {
      return "row";
    }
  });
  const setViewModePersist = (mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, mode);
    } catch {
      /* ignore quota / privacy errors */
    }
  };

  const setDays = (days) => onChange({ ...week, days });

  // Lookup race by day index using weekStartDate
  const raceForDayIdx = (dayIdx) => {
    if (!weekStartDate || !races || races.length === 0) return null;
    const base = new Date(weekStartDate + "T00:00:00");
    if (isNaN(base.getTime())) return null;
    base.setDate(base.getDate() + dayIdx);
    const iso = `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, "0")}-${String(base.getDate()).padStart(2, "0")}`;
    return races.find((r) => r.date === iso) || null;
  };

  // index completion days by date order to align with week.days[]
  const dayCompletionByIndex = (idx) => {
    if (!completion || !completion.available || !completion.days) return null;
    return completion.days[idx] || null;
  };

  const handleDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    )
      return;

    const newDays = week.days.map((d) => ({ ...d, workouts: [...(d.workouts || [])] }));
    const sourceDay = newDays.find((d) => d.id === source.droppableId);
    const destDay = newDays.find((d) => d.id === destination.droppableId);
    if (!sourceDay || !destDay) return;

    const [moved] = sourceDay.workouts.splice(source.index, 1);
    destDay.workouts.splice(destination.index, 0, moved);
    setDays(newDays);
  };

  const addWorkout = (dayId) => {
    const w = newWorkout();
    const newDays = week.days.map((d) =>
      d.id === dayId ? { ...d, workouts: [...(d.workouts || []), w] } : d
    );
    setDays(newDays);
    setEditing({ dayId, workoutId: w.id, workout: w });
  };

  const openEditor = (dayId, workout) =>
    setEditing({ dayId, workoutId: workout.id, workout });

  const saveWorkout = (updated) => {
    const newDays = week.days.map((d) =>
      d.id !== editing.dayId
        ? d
        : {
            ...d,
            workouts: (d.workouts || []).map((w) =>
              w.id === editing.workoutId ? { ...updated, id: w.id } : w
            ),
          }
    );
    setDays(newDays);
    setEditing(null);
  };

  // Quick toggle for the "Optional (bonus)" flag — invoked from the hover
  // button on the planner card so the coach doesn't have to open the full
  // editor for a one-flag change. Triggers the same `setDays` pipeline so
  // unsaved-changes tracking + autosave behave identically to the editor.
  const toggleWorkoutOptional = (dayId, workoutId) => {
    const newDays = week.days.map((d) =>
      d.id !== dayId
        ? d
        : {
            ...d,
            workouts: (d.workouts || []).map((w) =>
              w.id === workoutId ? { ...w, is_optional: !w.is_optional } : w
            ),
          }
    );
    setDays(newDays);
  };

  const deleteWorkout = () => {
    const newDays = week.days.map((d) =>
      d.id !== editing.dayId
        ? d
        : { ...d, workouts: (d.workouts || []).filter((w) => w.id !== editing.workoutId) }
    );
    setDays(newDays);
    setEditing(null);
  };

  // Inline removal — used by the small "×" button that lives directly on
  // Rest workout tiles. Rest tiles can't be expanded to reveal the full
  // Edit/Delete affordances (see the `disabled={isRest}` on the header
  // button in each view), so we ship a dedicated one-click delete so the
  // coach can rearrange rest days without popping a modal for each one.
  const removeWorkoutInline = (dayId, workoutId) => {
    const newDays = week.days.map((d) =>
      d.id !== dayId
        ? d
        : { ...d, workouts: (d.workouts || []).filter((w) => w.id !== workoutId) }
    );
    setDays(newDays);
  };

  // Copy this workout (with fresh IDs all the way down) to one or more other
  // days. New IDs are critical — without them, athletes marking one as
  // complete would also flip every duplicate to complete because logs key on
  // workout.id. uuid'd here so they stay independent forever.
  //
  // `opts.asAlternative` lets the coach decide whether each copy should be
  // tagged as an OR option on its target day. Default = false (clean fan-
  // out), opt-in if the coach wants the OR pattern.
  //
  // `opts.extraWeekNumbers` (optional) is a list of other week numbers to
  // also drop a copy into, on the SAME day labels. Bubbles up to the parent
  // since WeekCanvas only owns the current week.
  const duplicateToDays = (sourceWorkout, targetDayIds, opts = {}) => {
    const asAlt = !!opts.asAlternative;
    const extraWeekNumbers = (opts.extraWeekNumbers || []).filter(
      (n) => n !== currentWeekNumber
    );
    // Resolve in-week target day IDs → labels (for cross-week reuse). Skip
    // any in-week clone if no targets were ticked in the current week.
    let targetDayLabels = (week.days || [])
      .filter((d) => (targetDayIds || []).includes(d.id))
      .map((d) => d.label);
    // Cross-week fallback: if the coach picked extra weeks but no extra
    // days, mirror the workout's current day (so "Strength on Tue, also
    // in W2/W3" lands on Tue of W2/W3 without forcing an in-week clone).
    if (!targetDayLabels.length && extraWeekNumbers.length && editing) {
      const currentLabel = (week.days.find((d) => d.id === editing.dayId) || {}).label;
      if (currentLabel) targetDayLabels = [currentLabel];
    }

    const cloneWith = (n, path) => ({
      ...n,
      id: typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now(),
    });
    const cloneWorkout = (wo) => {
      const newWo = { ...wo, id: cloneWith().id, is_alternative: asAlt };
      // Re-id every segment and item recursively so drag-and-drop / log keys
      // never collide.
      const reidSeg = (seg) => ({
        ...seg,
        id: cloneWith().id,
        items: (seg.items || []).map(reidSeg),
      });
      newWo.segments = (wo.segments || []).map(reidSeg);
      newWo.strength_blocks = (wo.strength_blocks || []).map((b) => ({
        ...b,
        id: cloneWith().id,
        exercises: (b.exercises || []).map((ex) => ({ ...ex, id: cloneWith().id })),
      }));
      return newWo;
    };
    // Cross-week clones (handled by parent)
    if (extraWeekNumbers.length && targetDayLabels.length && onCrossWeekDuplicate) {
      onCrossWeekDuplicate(sourceWorkout, targetDayLabels, extraWeekNumbers, {
        asAlternative: asAlt,
      });
    }
    // In-week clones
    if (targetDayIds && targetDayIds.length) {
      const newDays = week.days.map((d) =>
        targetDayIds.includes(d.id)
          ? { ...d, workouts: [...(d.workouts || []), cloneWorkout(sourceWorkout)] }
          : d
      );
      setDays(newDays);
    }
    setEditing(null);
  };

  return (
    <div data-testid="week-canvas">
      {/* View toggle — Grid (default, week-at-a-glance) vs List (the same
          vertical day-row layout the athlete sees, useful when reviewing
          what a week looks like through the athlete's eyes). */}
      <div className="flex items-center justify-end mb-3 gap-2">
        <span className="text-[9px] tracking-[0.22em] uppercase font-mono-tir text-[var(--tir-muted)]">
          View
        </span>
        <div
          className="inline-flex border border-[var(--tir-border)] rounded-md overflow-hidden bg-white"
          data-testid="week-view-toggle"
          role="group"
          aria-label="Week view"
        >
          <button
            type="button"
            onClick={() => setViewModePersist("grid")}
            data-testid="week-view-grid"
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase font-semibold transition-colors ${
              viewMode === "grid"
                ? "bg-[var(--tir-navy)] text-white"
                : "text-[var(--tir-muted)] hover:text-[var(--tir-ink)]"
            }`}
            aria-pressed={viewMode === "grid"}
            title="Day-by-day, one row per weekday — best for editing detail"
          >
            <Grid3x3 size={11} /> Days
          </button>
          <button
            type="button"
            onClick={() => setViewModePersist("row")}
            data-testid="week-view-row"
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase font-semibold border-l border-[var(--tir-border)] transition-colors ${
              viewMode === "row"
                ? "bg-[var(--tir-navy)] text-white"
                : "text-[var(--tir-muted)] hover:text-[var(--tir-ink)]"
            }`}
            aria-pressed={viewMode === "row"}
            title="See the whole week at a glance — Mon → Sun across the screen"
          >
            <Columns size={11} /> Week
          </button>
          <button
            type="button"
            onClick={() => setViewModePersist("list")}
            data-testid="week-view-list"
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase font-semibold border-l border-[var(--tir-border)] transition-colors ${
              viewMode === "list"
                ? "bg-[var(--tir-navy)] text-white"
                : "text-[var(--tir-muted)] hover:text-[var(--tir-ink)]"
            }`}
            aria-pressed={viewMode === "list"}
            title="See the same single-column layout your athlete sees"
          >
            <List size={11} /> Athlete view
          </button>
        </div>
      </div>
      {viewMode === "list" ? (
        <CoachWeekListView
          week={week}
          races={races}
          weekStartDate={weekStartDate}
          startDay={startDay}
          logsLookup={logsLookup}
          openEditor={openEditor}
          setEditingLog={setEditingLog}
          athleteId={athleteId}
          programId={programId}
          additionsForDay={additionsForDay}
          shiftForWorkout={shiftForWorkout}
        />
      ) : viewMode === "row" ? (
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-2" data-testid="coach-week-row">
          {week.days.map((day, dayIdx) => {
            const dayComp = dayCompletionByIndex(dayIdx);
            const race = raceForDayIdx(dayIdx);
            const dayLabel = visualLabel(dayIdx);
            let dayDate = null;
            let dayIso = null;
            if (weekStartDate) {
              try {
                const d = new Date(weekStartDate + "T00:00:00");
                d.setDate(d.getDate() + dayIdx);
                if (!isNaN(d.getTime())) {
                  dayDate = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
                  dayIso = localIsoDate(d);
                }
              } catch { /* ignore */ }
            }
            const todayIso = localIsoDate(new Date());
            const isToday = dayIso === todayIso;
            return (
              <CoachDayColumn
                key={day.id}
                day={day}
                dayLabel={dayLabel}
                dayDate={dayDate}
                isToday={isToday}
                race={race}
                dayComp={dayComp}
                week={week}
                logsLookup={logsLookup}
                athleteId={athleteId}
                programId={programId}
                additionsForDay={additionsForDay}
                shiftForWorkout={shiftForWorkout}
                onOpenEditor={openEditor}
                onAddWorkout={() => addWorkout(day.id)}
                onToggleOptional={toggleWorkoutOptional}
                onRemoveWorkout={removeWorkoutInline}
                onEditLog={(workout, log) =>
                  setEditingLog({ workout, log, dayLabel: day.label })
                }
                onDayNoteChange={(text) => {
                  const newDays = week.days.map((d) =>
                    d.id === day.id ? { ...d, coach_note: text } : d
                  );
                  setDays(newDays);
                }}
              />
            );
          })}
        </div>
      </DragDropContext>
      ) : (
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex flex-col gap-2" data-testid="coach-week-grid">
          {week.days.map((day, dayIdx) => {
            const dayComp = dayCompletionByIndex(dayIdx);
            const race = raceForDayIdx(dayIdx);
            const dayLabel = visualLabel(dayIdx);
            // Compute the calendar date for this weekday so coach sees
            // e.g. "MON · May 18" on the day pill — matches athlete portal.
            let dayDate = null;
            let dayIso = null;
            if (weekStartDate) {
              try {
                const d = new Date(weekStartDate + "T00:00:00");
                d.setDate(d.getDate() + dayIdx);
                if (!isNaN(d.getTime())) {
                  dayDate = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
                  dayIso = localIsoDate(d);
                }
              } catch { /* ignore */ }
            }
            const todayIso = localIsoDate(new Date());
            const isToday = dayIso === todayIso;
            return (
              <CoachDayRow
                key={day.id}
                day={day}
                dayLabel={dayLabel}
                dayDate={dayDate}
                isToday={isToday}
                race={race}
                dayComp={dayComp}
                week={week}
                logsLookup={logsLookup}
                athleteId={athleteId}
                programId={programId}
                additionsForDay={additionsForDay}
                shiftForWorkout={shiftForWorkout}
                onOpenEditor={openEditor}
                onAddWorkout={() => addWorkout(day.id)}
                onToggleOptional={toggleWorkoutOptional}
                onRemoveWorkout={removeWorkoutInline}
                onEditLog={(workout, log) =>
                  setEditingLog({ workout, log, dayLabel: day.label })
                }
                onDayNoteChange={(text) => {
                  const newDays = week.days.map((d) =>
                    d.id === day.id ? { ...d, coach_note: text } : d
                  );
                  setDays(newDays);
                }}
              />
            );
          })}
        </div>
      </DragDropContext>
      )}

      {editing && (
        <WorkoutEditor
          workout={editing.workout}
          onSave={saveWorkout}
          onClose={() => setEditing(null)}
          onDelete={deleteWorkout}
          onDuplicate={duplicateToDays}
          currentDayLabel={(week.days.find((d) => d.id === editing.dayId) || {}).label}
          weekDays={week.days}
          athletePaces={athletePaces}
          paceSource={paceSource}
          athleteId={athleteId}
          programId={programId}
          allWeeks={allWeeks}
          currentWeekNumber={currentWeekNumber}
        />
      )}

      {editingLog && (
        <CoachWorkoutLogOverrideModal
          athleteId={athleteId}
          programId={programId}
          weekNumber={currentWeekNumber || week.week_number}
          dayLabel={editingLog.dayLabel}
          workoutId={editingLog.workout.id}
          workout={editingLog.workout}
          existingLog={editingLog.log || null}
          onEditWorkout={() => {
            const d = (week.days || []).find((x) => x.label === editingLog.dayLabel);
            setEditingLog(null);
            if (d) openEditor(d.id, editingLog.workout);
          }}
          onClose={(result) => {
            setEditingLog(null);
            if (result && typeof onLogChanged === "function") {
              onLogChanged();
            }
          }}
        />
      )}
    </div>
  );
}

/**
 * DayNoteInput — collapsed by default to keep day columns compact.
 *
 * Three modes:
 *   1. No note yet → tiny "+ day note" button.
 *   2. Note exists but not editing → 2-line preview + "Edit" toggle.
 *   3. Editing → full RichTextEditor with "Done" button.
 *
 * Previously the rich-text editor was mounted permanently whenever a note
 * had content. With toolbar + min-height + borders that's ~100px of
 * vertical space *per day column*, which blew up the Week grid (especially
 * the horizontal 7-col view). Coaches reported it dominating the screen.
 */
function DayNoteInput({ day, onChange }) {
  const [editing, setEditing] = React.useState(false);
  const [val, setVal] = React.useState(day.coach_note || "");
  // Don't overwrite the local `val` while the coach is actively editing —
  // an autosave round-trip can re-emit the same `day.coach_note` and stomp
  // characters that the coach just typed (felt as "caret jumps to the
  // top"). Only resync when not editing OR when switching to a different
  // day (different `day.id`).
  const editingRef = React.useRef(false);
  React.useEffect(() => { editingRef.current = editing; }, [editing]);
  React.useEffect(() => {
    if (editingRef.current) return;
    setVal(day.coach_note || "");
  }, [day.id, day.coach_note]);
  const hasNote = !!String(val || "").replace(/<[^>]*>/g, "").trim();

  // Mode 1 — empty + not editing → compact "+" affordance.
  if (!editing && !hasNote) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="px-3 py-1 text-[9px] tracking-[0.22em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] text-left border-b border-[var(--tir-border)]"
        data-testid={`day-${day.label}-note-add`}
      >
        + day note
      </button>
    );
  }

  // Mode 2 — note exists, read-only preview.
  if (!editing && hasNote) {
    return (
      <div
        className="px-3 py-1.5 border-b border-[var(--tir-border)] bg-[#FAF7F2] flex items-start gap-2"
        data-testid={`day-${day.label}-note-preview-wrap`}
      >
        <div className="flex-1 min-w-0">
          <div className="text-[8px] tracking-[0.22em] uppercase text-[var(--tir-burgundy)] font-semibold mb-0.5">
            Coach note
          </div>
          <RichTextView
            html={val}
            className="text-[11px] leading-snug text-[var(--tir-ink)] line-clamp-2 [&_p]:m-0 [&_p+p]:mt-0.5 [&_ul]:m-0 [&_ol]:m-0"
            testid={`day-${day.label}-note-preview`}
          />
        </div>
        <div className="shrink-0 flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-[8px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy-dark)]"
            data-testid={`day-${day.label}-note-edit`}
          >
            Edit
          </button>
          <button
            type="button"
            onClick={() => {
              setVal("");
              onChange(null);
            }}
            className="text-[8px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)]"
            data-testid={`day-${day.label}-note-clear`}
            title="Clear this day note"
          >
            Clear
          </button>
        </div>
      </div>
    );
  }

  // Mode 3 — actively editing the note.
  return (
    <div className="px-3 py-2 border-b border-[var(--tir-border)] bg-[#FAF7F2]">
      <div className="flex items-baseline justify-between mb-1">
        <div className="text-[9px] tracking-[0.22em] uppercase text-[var(--tir-burgundy)] font-semibold">
          Coach note
        </div>
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="text-[8px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy-dark)]"
          data-testid={`day-${day.label}-note-done`}
        >
          Done
        </button>
      </div>
      <RichTextEditor
        value={val}
        onChange={(html) => {
          setVal(html);
          const trimmed = (html || "").replace(/<[^>]*>/g, "").trim();
          onChange(trimmed ? html : null);
        }}
        rows={2}
        toolbarCompact
        testid={`day-${day.label}-note-input`}
        placeholder="e.g. → Pick whichever fits your morning"
      />
    </div>
  );
}

/**
 * CoachWeekListView — alternate to the 7-col grid.
 *
 * Renders the same week as a vertical day-by-day list mirroring the
 * athlete portal. Click a workout to open the editor (same handler as the
 * grid view). No drag-and-drop here on purpose — the grid is the
 * drag/reorder surface; the list view is a "what the athlete sees" view.
 */
function CoachWeekListView({ week, races, weekStartDate, startDay, logsLookup, openEditor, setEditingLog, athleteId, programId, additionsForDay, shiftForWorkout }) {
  const labels = (() => {
    if (startDay === "sun") return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    if (startDay === "day1") return ["Day 1", "Day 2", "Day 3", "Day 4", "Day 5", "Day 6", "Day 7"];
    return ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  })();
  const todayIso = localIsoDate(new Date());
  const dayDateAt = (di) => {
    if (!weekStartDate) return { iso: null, label: null };
    try {
      const d = new Date(weekStartDate + "T00:00:00");
      d.setDate(d.getDate() + di);
      if (isNaN(d.getTime())) return { iso: null, label: null };
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      return {
        iso,
        label: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      };
    } catch {
      return { iso: null, label: null };
    }
  };
  const raceForDayIdx = (di) => {
    const { iso } = dayDateAt(di);
    if (!iso || !races || !races.length) return null;
    return races.find((r) => r.date === iso) || null;
  };
  return (
    <div className="flex flex-col gap-2" data-testid="coach-week-list">
      {(week.days || []).map((day, di) => {
        const dayLabel = labels[di] || day.label;
        const { iso: dayIso, label: dayDate } = dayDateAt(di);
        const isToday = dayIso === todayIso;
        const race = raceForDayIdx(di);
        const workouts = day.workouts || [];
        const allRest = workouts.length === 0 || workouts.every((w) => w.type === "rest");
        return (
          <div
            key={day.id}
            className="flex flex-col gap-1"
            data-testid={`coach-day-row-${day.label}`}
          >
            {day.coach_note && (
              <div
                className="bg-[#FAF7F2] border-l-4 border-[var(--tir-burgundy)] px-4 py-2.5 rounded-r-md"
                data-testid={`coach-day-${day.label}-note`}
              >
                <div className="text-[9px] tracking-[0.22em] uppercase text-[var(--tir-burgundy)] font-semibold mb-1">
                  {dayLabel} · Coach's note
                </div>
                <div
                  className="text-[12px] leading-snug text-[var(--tir-ink)] [&_p]:m-0 [&_p+p]:mt-1"
                  dangerouslySetInnerHTML={{ __html: day.coach_note }}
                />
              </div>
            )}
            {race && (() => {
              const raceWorkoutId = `race:${race.id}`;
              const raceKey = `${week.week_number}-${day.label}-${raceWorkoutId}`;
              let raceLog = logsLookup ? logsLookup[raceKey] : null;
              if (!raceLog && logsLookup) {
                const prefix = `${week.week_number}-${day.label}-`;
                for (const [k, v] of Object.entries(logsLookup)) {
                  if (k.startsWith(prefix) && k !== raceKey && v) {
                    raceLog = v;
                    break;
                  }
                }
              }
              const status = raceLog?.status;
              const raceWorkout = {
                id: raceWorkoutId,
                type: "race",
                title: race.name || "Race",
                duration_or_distance: race.distance || "",
                is_race: true,
                race_priority: race.priority || null,
                session_note: race.notes || "",
              };
              return (
              <div
                role="button"
                tabIndex={0}
                onClick={() => athleteId && programId && setEditingLog({
                  workout: raceWorkout, log: raceLog, dayLabel: day.label,
                })}
                onKeyDown={(e) => {
                  if (athleteId && programId && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    setEditingLog({ workout: raceWorkout, log: raceLog, dayLabel: day.label });
                  }
                }}
                className={`flex items-stretch min-h-[60px] bg-[#fdf6e7] rounded-md border border-[var(--tir-burgundy)] overflow-hidden ${
                  athleteId && programId ? "cursor-pointer hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[var(--tir-burgundy)]/40" : ""
                }`}
                data-testid={`coach-list-race-${day.label}`}
                aria-label={`${race.name} — click to log on athlete's behalf`}
              >
                <div className="w-16 md:w-20 shrink-0 flex flex-col items-center justify-center bg-black/[0.04] border-r border-[var(--tir-burgundy)]/30 py-2">
                  <div className="font-serif-display text-xl uppercase text-[var(--tir-ink)] leading-none tracking-wide">{dayLabel}</div>
                  {dayDate && <div className="text-[10px] font-mono-tir text-[var(--tir-muted)] mt-1">{dayDate}</div>}
                </div>
                <div className="flex-1 flex flex-col justify-center px-4 py-3">
                  <div className="flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase font-bold text-[var(--tir-burgundy)]">
                    <Trophy size={11} />
                    {race.priority ? `Race · ${race.priority}` : "Race"}
                    {status === "complete" && <Check size={12} color="#2F855A" className="ml-1" />}
                    {status === "partial" && <AlertTriangle size={12} color="#D69E2E" className="ml-1" />}
                    {status === "skipped" && <X size={12} color="#7A1F2B" className="ml-1" />}
                  </div>
                  <div className="text-[14px] font-semibold text-[var(--tir-ink)] mt-1">{race.name}</div>
                  <div className="flex items-center gap-3 mt-0.5 text-[11px] font-mono-tir text-[var(--tir-muted)]">
                    {race.distance && <span>{race.distance}</span>}
                    {raceLog && raceLog.distance_actual_km > 0 && (
                      <span>· {Number(raceLog.distance_actual_km).toFixed(1)} km logged</span>
                    )}
                    {raceLog && raceLog.duration_actual_min > 0 && (
                      <span>· {Math.round(raceLog.duration_actual_min)} min</span>
                    )}
                    {raceLog && raceLog.rpe_actual != null && <span>· RPE {raceLog.rpe_actual}</span>}
                  </div>
                </div>
              </div>
              );
            })()}
            {allRest && !race ? (
              <div
                className={`flex items-stretch min-h-[64px] border border-dashed rounded-md bg-white/50 overflow-hidden ${
                  isToday ? "border-[var(--tir-navy)] border-solid" : "border-[var(--tir-border)]"
                }`}
                data-testid={`coach-rest-row-${day.label}`}
              >
                <div className="w-16 md:w-20 shrink-0 flex flex-col items-center justify-center bg-black/[0.03] border-r border-[var(--tir-border)] py-2">
                  <div className="font-serif-display text-xl uppercase text-[var(--tir-ink)] leading-none tracking-wide">{dayLabel}</div>
                  {dayDate && <div className="text-[10px] font-mono-tir text-[var(--tir-muted)] mt-1">{dayDate}</div>}
                  {isToday && <div className="text-[8px] tracking-[0.22em] uppercase font-bold text-[var(--tir-navy)] mt-1">Today</div>}
                </div>
                <div className="flex-1 flex items-center justify-center px-4 py-3">
                  <span className="text-[12px] italic text-[var(--tir-muted)]">Rest</span>
                </div>
              </div>
            ) : (
              workouts.map((w, woIdx) => {
                const log = logsLookup ? logsLookup[`${week.week_number}-${day.label}-${w.id}`] : null;
                const statusStyles = {
                  complete: { bar: "#2F855A", bg: "#F0FDF4" },
                  partial: { bar: "#D69E2E", bg: "#FEFCE8" },
                  skipped: { bar: "#7A1F2B", bg: "#FDF2F2" },
                };
                const palette = statusStyles[log?.status] || null;
                const isRest = w.type === "rest";
                return (
                  <React.Fragment key={w.id}>
                    {woIdx > 0 && w.is_alternative && (
                      <div className="flex items-center gap-2 pl-20 -my-0.5">
                        <div className="flex-1 h-px bg-[var(--tir-border)]" />
                        <span className="text-[9px] tracking-[0.28em] uppercase font-bold text-[var(--tir-burgundy)]">OR</span>
                        <div className="flex-1 h-px bg-[var(--tir-border)]" />
                      </div>
                    )}
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => (log && athleteId && programId ? setEditingLog({ workout: w, log, dayLabel: day.label }) : openEditor(day.id, w))}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          if (log && athleteId && programId) setEditingLog({ workout: w, log, dayLabel: day.label });
                          else openEditor(day.id, w);
                        }
                      }}
                      data-testid={`coach-list-workout-${w.id}`}
                      className={`relative flex items-stretch min-h-[80px] bg-white rounded-md overflow-hidden border cursor-pointer transition-all duration-200 hover:shadow-md hover:border-[var(--tir-burgundy)]/40 ${
                        isToday && !palette
                          ? "border-[var(--tir-navy)] border-2"
                          : w.is_alternative
                            ? "border-dashed border-[var(--tir-burgundy)]/50"
                            : "border-[var(--tir-border)]"
                      }`}
                    >
                      <div className="w-16 md:w-20 shrink-0 flex flex-col items-center justify-center bg-black/[0.03] border-r border-[var(--tir-border)] py-2 z-10">
                        <div className="font-serif-display text-xl md:text-2xl uppercase text-[var(--tir-ink)] leading-none tracking-wide">{dayLabel}</div>
                        {dayDate && <div className="text-[10px] font-mono-tir text-[var(--tir-muted)] mt-1">{dayDate}</div>}
                      </div>
                      {palette && (
                        <div className="w-2 shrink-0" style={{ backgroundColor: palette.bar }} aria-hidden="true" />
                      )}
                      <div
                        className="flex-1 flex flex-col justify-center py-3 px-4 gap-1 min-w-0"
                        style={palette ? { backgroundColor: palette.bg } : undefined}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="text-[14px] md:text-[15px] font-semibold text-[var(--tir-ink)] leading-snug flex-1 min-w-0">
                            {w.is_alternative && <span className="text-[var(--tir-burgundy)] mr-1">↳</span>}
                            {w.is_optional && (
                              <span className="inline-block text-[8px] tracking-[0.2em] uppercase font-bold text-[var(--tir-navy)] border border-[var(--tir-navy)]/40 px-1.5 mr-1.5 align-middle rounded-sm">
                                Optional
                              </span>
                            )}
                            {w.title || w.type}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {(() => {
                              const s = shiftForWorkout(w.id);
                              return s ? (
                                <span
                                  className="text-[8px] tracking-[0.18em] uppercase font-bold text-white bg-[var(--tir-navy)] px-1.5 py-0.5 rounded-sm"
                                  data-testid={`coach-shift-badge-${w.id}`}
                                  title="The athlete moved this workout on their portal"
                                >
                                  Moved → {s.to_day}
                                </span>
                              ) : null;
                            })()}
                            {isToday && !log && (
                              <span className="text-[8px] tracking-[0.22em] uppercase font-bold text-white bg-[var(--tir-navy)] px-1.5 py-0.5 rounded-sm">Today</span>
                            )}
                            {log?.status === "complete" && <Check size={14} color="#2F855A" />}
                            {log?.status === "partial" && <AlertTriangle size={14} color="#D69E2E" />}
                            {log?.status === "skipped" && <X size={14} color="#7A1F2B" />}
                          </div>
                        </div>
                        {(w.duration_or_distance || w.structure || (w.zones && w.zones.length > 0)) && (
                          <div className="text-[11px] font-mono-tir text-[var(--tir-muted)] tracking-wide flex flex-wrap gap-x-3 gap-y-0.5">
                            {w.duration_or_distance && <span>{w.duration_or_distance}</span>}
                            {w.zones && w.zones.length > 0 && <span>{w.zones.join(" · ")}</span>}
                            {w.rpe_min != null && w.rpe_max != null && <span>RPE {w.rpe_min}-{w.rpe_max}</span>}
                          </div>
                        )}
                        {w.structure && (
                          <div className="text-[11px] text-[var(--tir-muted)] leading-snug line-clamp-2">{w.structure}</div>
                        )}
                        {log?.athlete_note && (
                          <div className="text-[11px] text-[var(--tir-muted)] italic mt-1 line-clamp-2 border-t border-dashed border-[var(--tir-border)] pt-1">
                            "{log.athlete_note}"
                          </div>
                        )}
                        {log && <div className="mt-1"><LogPhotoStrip log={log} size="xs" testid={`coach-list-photos-${w.id}`} /></div>}
                        {log && athleteId && programId && !isRest && (
                          <div className="mt-1">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingLog({ workout: w, log, dayLabel: day.label });
                              }}
                              data-testid={`coach-list-override-${w.id}`}
                              className="text-[9px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy-dark)]"
                            >
                              Edit log →
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })
            )}
            {additionsForDay(day.label).map((a) => {
              const aLog = logsLookup ? logsLookup[`${week.week_number}-${day.label}-${a.id}`] : null;
              const aWorkout = {
                id: a.id, type: a.type || "cross", title: a.title,
                duration_or_distance: a.duration_or_distance || "",
                notes: a.notes || "", segments: [], added_by_athlete: true,
              };
              return (
                <div
                  key={a.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => athleteId && programId && setEditingLog({ workout: aWorkout, log: aLog, dayLabel: day.label })}
                  className={`flex items-stretch min-h-[56px] bg-[var(--tir-navy)]/[0.04] rounded-md border border-dashed border-[var(--tir-navy)]/50 overflow-hidden ${
                    athleteId && programId ? "cursor-pointer hover:shadow-md" : ""
                  }`}
                  data-testid={`coach-athlete-added-${a.id}`}
                >
                  <div className="w-16 md:w-20 shrink-0 flex flex-col items-center justify-center bg-black/[0.03] border-r border-[var(--tir-border)] py-2">
                    <div className="font-serif-display text-xl uppercase text-[var(--tir-ink)] leading-none tracking-wide">{dayLabel}</div>
                    {dayDate && <div className="text-[10px] font-mono-tir text-[var(--tir-muted)] mt-1">{dayDate}</div>}
                  </div>
                  <div className="flex-1 flex flex-col justify-center px-4 py-2.5 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[8px] tracking-[0.2em] uppercase font-bold text-white bg-[var(--tir-navy)] px-1.5 py-0.5 rounded-sm">
                        Added by athlete
                      </span>
                      <span className="text-[13px] font-semibold text-[var(--tir-ink)] truncate">{a.title}</span>
                      {aLog?.status === "complete" && <Check size={13} color="#2F855A" />}
                      {aLog?.status === "partial" && <AlertTriangle size={13} color="#D69E2E" />}
                      {aLog?.status === "skipped" && <X size={13} color="#7A1F2B" />}
                    </div>
                    <div className="text-[11px] font-mono-tir text-[var(--tir-muted)] tracking-wide mt-0.5 flex gap-x-3">
                      {a.duration_or_distance && <span>{a.duration_or_distance}</span>}
                      <span>{a.type}</span>
                    </div>
                    {a.notes && (
                      <div className="text-[11px] text-[var(--tir-muted)] italic mt-0.5 line-clamp-2">"{a.notes}"</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}



// --- Helpers for the new vertical day-row Grid view ----------------------
const ICON_BY_NAME = {
  Footprints, Dumbbell, Bike, Mountain, Trophy, Flag, Zap, PersonStanding, Activity, Moon,
};
function getWorkoutIcon(type) {
  const meta = WORKOUT_TYPE_MAP[type];
  return ICON_BY_NAME[meta?.icon || "Activity"] || Activity;
}
function getWorkoutAccent(type) {
  return WORKOUT_TYPE_MAP[type]?.accent || "#6F6A66";
}

/**
 * localIsoDate — format a Date as a local-time YYYY-MM-DD. We canNOT
 * use `d.toISOString().slice(0, 10)` for "today" or for week day-of
 * comparisons because that's UTC; an EDT user opening the app after
 * 8 PM would have today roll forward to tomorrow UTC, mis-labeling
 * the next day as TODAY in the planner. Always reach for local
 * Y/M/D when we're comparing calendar days the user actually sees.
 */
function localIsoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * FeelingChip — readonly pill showing the athlete's RPE-feel gauge value
 * (very_weak..very_strong) as a tiny lucide smiley + colored label. Used
 * inline under logged workouts in the coach's planner so the coach can
 * scan how everyone felt without opening each log modal. Mirrors the
 * 5-stop scale set in AthleteWorkoutLogModal::FEELINGS.
 */
const FEELING_META = {
  very_weak:   { Icon: Angry, label: "Very weak",   color: "#B5371E" },
  weak:        { Icon: Frown, label: "Weak",        color: "#B97816" },
  normal:      { Icon: Meh,   label: "Normal",      color: "#6F6A66" },
  strong:      { Icon: Smile, label: "Strong",      color: "#2F855A" },
  very_strong: { Icon: Laugh, label: "Very strong", color: "#16734A" },
};
function FeelingChip({ value, compact = false, testid }) {
  const meta = FEELING_META[value];
  if (!meta) return null;
  const { Icon, label, color } = meta;
  const sz = compact ? 10 : 12;
  return (
    <span
      className="inline-flex items-center gap-1 font-semibold"
      style={{ color, fontSize: compact ? 10 : 11 }}
      title={`Athlete felt ${label.toLowerCase()}`}
      data-testid={testid || `feeling-chip-${value}`}
    >
      <Icon size={sz} strokeWidth={2.2} />
      {!compact && <span>{label}</span>}
    </span>
  );
}

/**
 * InlineCoachReply — single-line input that posts a comment to the
 * workout's thread via `coachPostWorkoutComment`. Surfaces the existing
 * conversation under each logged workout so the coach can fire off a
 * one-liner without opening the override modal. Athlete gets the same
 * email notification as a reply from the full thread modal.
 */
function InlineCoachReply({ athleteId, programId, weekNumber, dayLabel, workoutId, testidSuffix }) {
  const [draft, setDraft] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  if (!athleteId || !programId) return null;
  const submit = async (e) => {
    e?.preventDefault?.();
    const text = (draft || "").trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      await coachPostWorkoutComment(athleteId, {
        program_id: programId,
        week_number: weekNumber,
        day_label: dayLabel,
        workout_id: workoutId,
        body: text,
      });
      setDraft("");
      toast.success("Reply sent — athlete will get an email");
    } catch {
      toast.error("Couldn't send — please try again");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      onSubmit={submit}
      className="flex items-center gap-1.5 mt-1"
      data-testid={`inline-reply-${testidSuffix}`}
      // Stop propagation so the parent toggle/expand handlers don't fire
      // when the coach clicks inside the input.
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          submit();
        }
      }}
    >
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Quick reply…"
        data-testid={`inline-reply-input-${testidSuffix}`}
        className="flex-1 min-w-0 border border-[var(--tir-border)] bg-white px-2 py-1 text-[11px] focus:outline-none focus:border-[var(--tir-burgundy)] rounded-sm leading-snug"
      />
      <button
        type="submit"
        disabled={!draft.trim() || busy}
        data-testid={`inline-reply-send-${testidSuffix}`}
        className="inline-flex items-center gap-1 text-[9px] tracking-[0.18em] uppercase font-semibold px-2 py-1 bg-[var(--tir-burgundy)] text-white hover:bg-[var(--tir-burgundy-dark)] disabled:opacity-40 disabled:cursor-not-allowed rounded-sm"
        title="Send reply (athlete gets an email)"
      >
        <Send size={9} />
      </button>
    </form>
  );
}

/**
 * CoachDayRow — single-line-per-day Coach grid row.
 *
 * Visually mirrors the athlete portal day rows (day-pill on the left,
 * colored workout tiles on the right, click-to-expand inline details)
 * with a few coach-only affordances:
 *   - Drag-handle (≡ grip) on each workout tile so reordering / cross-
 *     day moves still work via @hello-pangea/dnd.
 *   - Inline `+ Workout` button to add a new workout to the day.
 *   - Per-day coach private note input.
 *   - Expanded panel surfaces athlete's logged note + photo + RPE +
 *     "Edit log" override AND "Edit workout" buttons at the bottom.
 *
 * The athlete's static portal view is rendered via CoachWeekListView; this
 * is the COACH GRID redesign — it stays drag/drop-friendly because Natalie
 * builds programs in this mode.
 */
function CoachDayRow({
  day, dayLabel, dayDate, isToday, race, dayComp, week, logsLookup,
  athleteId, programId, onOpenEditor, onAddWorkout, onToggleOptional,
  onEditLog, onDayNoteChange, onRemoveWorkout, additionsForDay, shiftForWorkout,
}) {
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));
  const workouts = day.workouts || [];
  const allRest = workouts.length === 0 || workouts.every((w) => w.type === "rest");
  const boxBorder = isToday ? "border-[var(--tir-navy)] border-2" : "border-[var(--tir-border)]";

  return (
    <div data-testid={`coach-day-row-${day.label}`}>
      {/* Day-level coach note — coach-editable, slim header above the row */}
      <DayNoteInput day={day} onChange={onDayNoteChange} />
      {race && (() => {
        // Race tile is now a real clickable workout for the coach. We look
        // up the log under the synthetic `race:<rid>` workout_id first
        // (athlete logged directly on the race, or Strava auto-matched
        // after the backend fix). Failing that we fall back to ANY log
        // on this day so a regular-slot log on race day still shows
        // green check + km/min on the race tile — coaches reported the
        // race tile sitting empty even though the athlete had logged
        // the same day via the regular workout slot.
        const raceWorkoutId = `race:${race.id}`;
        const raceKey = `${week.week_number}-${day.label}-${raceWorkoutId}`;
        let raceLog = logsLookup ? logsLookup[raceKey] : null;
        if (!raceLog && logsLookup) {
          const prefix = `${week.week_number}-${day.label}-`;
          for (const [k, v] of Object.entries(logsLookup)) {
            if (k.startsWith(prefix) && k !== raceKey && v) {
              raceLog = v;
              break;
            }
          }
        }
        const raceStatus = raceLog?.status;
        const raceWorkout = {
          id: raceWorkoutId,
          type: "race",
          title: race.name || "Race",
          duration_or_distance: race.distance || "",
          is_race: true,
          race_priority: race.priority || null,
          session_note: race.notes || "",
        };
        return (
        <div
          role="button"
          tabIndex={0}
          onClick={() => {
            if (athleteId && programId) {
              onEditLog(raceWorkout, raceLog);
            }
          }}
          onKeyDown={(e) => {
            if (athleteId && programId && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              onEditLog(raceWorkout, raceLog);
            }
          }}
          className={`flex items-stretch bg-[#fdf6e7] rounded-md border border-[var(--tir-burgundy)] overflow-hidden mb-1 transition-shadow ${
            athleteId && programId ? "cursor-pointer hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[var(--tir-burgundy)]/40" : ""
          }`}
          data-testid={`coach-grid-race-${day.label}`}
          aria-label={`${race.name} — click to log on athlete's behalf`}
        >
          <div className="w-16 md:w-20 shrink-0 flex flex-col items-center justify-center bg-black/[0.04] border-r border-[var(--tir-burgundy)]/30 py-2">
            <div className="font-serif-display text-xl uppercase text-[var(--tir-ink)] leading-none tracking-wide">{dayLabel}</div>
            {dayDate && <div className="text-[10px] font-mono-tir text-[var(--tir-muted)] mt-1">{dayDate}</div>}
          </div>
          <div className="flex-1 flex flex-col justify-center px-4 py-3">
            <div className="flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase font-bold text-[var(--tir-burgundy)]">
              <Trophy size={11} />
              {race.priority ? `Race · ${race.priority}` : "Race"}
              {raceStatus === "complete" && <Check size={12} color="#2F855A" className="ml-1" />}
              {raceStatus === "partial" && <AlertTriangle size={12} color="#D69E2E" className="ml-1" />}
              {raceStatus === "skipped" && <X size={12} color="#7A1F2B" className="ml-1" />}
            </div>
            <div className="text-[14px] font-semibold text-[var(--tir-ink)] mt-1">{race.name}</div>
            <div className="flex items-center gap-3 mt-1 text-[11px] font-mono-tir text-[var(--tir-muted)]">
              {race.distance && <span>{race.distance}</span>}
              {raceLog && raceLog.distance_actual_km > 0 && (
                <span data-testid={`coach-grid-race-${day.label}-km`}>
                  · {Number(raceLog.distance_actual_km).toFixed(1)} km logged
                </span>
              )}
              {raceLog && raceLog.duration_actual_min > 0 && (
                <span>· {Math.round(raceLog.duration_actual_min)} min</span>
              )}
              {raceLog && raceLog.rpe_actual != null && (
                <span>· RPE {raceLog.rpe_actual}</span>
              )}
              {raceLog && raceLog.source === "strava_auto" && (
                <span className="text-[#fc4c02] font-semibold">· ✦ Strava</span>
              )}
            </div>
            {athleteId && programId && (
              <div className="text-[9px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-burgundy)] mt-1.5">
                {raceLog ? "click to edit log" : "click to log race"}
              </div>
            )}
          </div>
        </div>
        );
      })()}
      <div
        className={`flex items-stretch bg-white rounded-md overflow-hidden border ${boxBorder} ${
          allRest && !race ? "border-dashed bg-white/50" : ""
        }`}
      >
        {/* Day-pill (shared across all workouts in this day) — kept
            OUTSIDE the Droppable so it's not treated as a drop slot. */}
        <div className="w-16 md:w-20 shrink-0 flex flex-col items-center justify-center bg-black/[0.03] border-r border-[var(--tir-border)] py-3 z-[1]">
          <div className="font-serif-display text-xl md:text-2xl uppercase text-[var(--tir-ink)] leading-none tracking-wide">
            {dayLabel}
          </div>
          {dayDate && (
            <div className="text-[10px] font-mono-tir text-[var(--tir-muted)] mt-1">{dayDate}</div>
          )}
          {isToday && (
            <div className="text-[8px] tracking-[0.22em] uppercase font-bold text-[var(--tir-navy)] mt-1.5">
              Today
            </div>
          )}
          {dayComp?.status && <div className="mt-2"><DayStatusDot status={dayComp.status} /></div>}
        </div>
        <Droppable droppableId={day.id}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`flex-1 flex flex-col min-w-0 divide-y divide-[var(--tir-border)] ${
                snapshot.isDraggingOver ? "bg-[var(--tir-burgundy)]/5" : ""
              }`}
            >
              {allRest && !race && workouts.length === 0 ? null : workouts.map((wo, woIdx) => {
                const log = logsLookup ? logsLookup[`${week.week_number}-${day.label}-${wo.id}`] : null;
                const isRest = wo.type === "rest";
                const status = log?.status;
                const statusStyles = {
                  complete: { bar: "#2F855A", bg: "#F0FDF4" },
                  partial: { bar: "#D69E2E", bg: "#FEFCE8" },
                  skipped: { bar: "#7A1F2B", bg: "#FDF2F2" },
                };
                const palette = statusStyles[status] || null;
                const accent = getWorkoutAccent(wo.type);
                const Icon = getWorkoutIcon(wo.type);
                const isExpanded = !!expanded[wo.id];
                const typeLabel = WORKOUT_TYPE_MAP[wo.type]?.label || wo.type;
                return (
                  <React.Fragment key={wo.id}>
                    {woIdx > 0 && wo.is_alternative && (
                      <div className="flex items-center gap-2 px-3 py-0.5 bg-white">
                        <div className="flex-1 h-px bg-[var(--tir-border)]" />
                        <span className="text-[9px] tracking-[0.28em] uppercase font-bold text-[var(--tir-burgundy)]">OR</span>
                        <div className="flex-1 h-px bg-[var(--tir-border)]" />
                      </div>
                    )}
                    <Draggable draggableId={wo.id} index={woIdx} isDragDisabled={isRest}>
                      {(dragProvided, dragSnapshot) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          className={`flex flex-col relative ${dragSnapshot.isDragging ? "shadow-lg bg-white" : ""}`}
                          data-testid={`coach-grid-workout-${wo.id}`}
                        >
                          <div className="flex items-stretch">
                          {/* Status / accent stripe */}
                          <div
                            className="w-2 shrink-0"
                            style={{ backgroundColor: palette ? palette.bar : accent }}
                            aria-hidden="true"
                          />
                          {/* Drag handle */}
                          <div
                            {...dragProvided.dragHandleProps}
                            className="flex items-center justify-center px-1.5 text-[var(--tir-muted)] hover:text-[var(--tir-ink)] cursor-grab active:cursor-grabbing"
                            aria-label="Reorder workout"
                            title="Drag to reorder or move to another day"
                            data-testid={`coach-grid-drag-${wo.id}`}
                          >
                            <GripVertical size={14} />
                          </div>
                          {/* Header (always visible) — click toggles expand */}
                          <button
                            type="button"
                            onClick={() => !isRest && toggle(wo.id)}
                            disabled={isRest}
                            aria-expanded={isExpanded}
                            data-testid={`coach-grid-toggle-${wo.id}`}
                            className={`flex-1 flex items-center gap-3 px-3 py-3 text-left min-w-0 ${
                              isRest ? "cursor-default" : "cursor-pointer hover:bg-black/[0.02]"
                            }`}
                            style={palette ? { backgroundColor: palette.bg } : undefined}
                          >
                            <div
                              className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: `${accent}15`, color: accent }}
                              aria-hidden="true"
                            >
                              <Icon size={18} strokeWidth={2} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                {wo.is_alternative && (
                                  <span className="text-[var(--tir-burgundy)]">↳</span>
                                )}
                                {wo.is_optional && (
                                  <span
                                    className="inline-block text-[8px] tracking-[0.2em] uppercase font-bold text-[var(--tir-navy)] border border-[var(--tir-navy)]/40 px-1.5 rounded-sm"
                                    data-testid={`coach-grid-optional-badge-${wo.id}`}
                                  >
                                    Optional
                                  </span>
                                )}
                                <span className="text-[14px] md:text-[15px] font-semibold text-[var(--tir-ink)] leading-snug truncate">
                                  {wo.title || typeLabel}
                                </span>
                              </div>
                              {(wo.duration_or_distance || (!wo.title && typeLabel)) && (
                                <div className="text-[11px] font-mono-tir text-[var(--tir-muted)] tracking-wide mt-0.5 truncate">
                                  {wo.duration_or_distance ? wo.duration_or_distance : ""}
                                  {wo.duration_or_distance && wo.title ? " · " : ""}
                                  {wo.title ? typeLabel : ""}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {(() => {
                                const s = shiftForWorkout?.(wo.id);
                                return s ? (
                                  <span
                                    className="text-[8px] tracking-[0.18em] uppercase font-bold text-white bg-[var(--tir-navy)] px-1.5 py-0.5 rounded-sm"
                                    data-testid={`coach-grid-shift-badge-${wo.id}`}
                                    title="The athlete moved this workout on their portal"
                                  >
                                    Moved → {s.to_day}
                                  </span>
                                ) : null;
                              })()}
                              {isToday && !status && (
                                <span className="text-[8px] tracking-[0.22em] uppercase font-bold text-white bg-[var(--tir-navy)] px-1.5 py-0.5 rounded-sm">
                                  Today
                                </span>
                              )}
                              {status === "complete" && <Check size={14} color="#2F855A" />}
                              {status === "partial" && <AlertTriangle size={14} color="#D69E2E" />}
                              {status === "skipped" && <X size={14} color="#7A1F2B" />}
                              {!isRest && (
                                <ChevronDown
                                  size={16}
                                  className="text-[var(--tir-muted)] transition-transform"
                                  style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)" }}
                                />
                              )}
                            </div>
                          </button>
                          {/* Inline "remove rest slot" X button — Rest
                              tiles are the only place we surface this
                              because they can't be expanded to reach the
                              editor's Delete button (see the disabled
                              header click for isRest). Rendered as a
                              sibling of the header <button> so its click
                              doesn't trigger the parent expand handler. */}
                          {isRest && onRemoveWorkout && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm("Remove this rest slot?")) {
                                  onRemoveWorkout(day.id, wo.id);
                                }
                              }}
                              data-testid={`coach-grid-remove-rest-${wo.id}`}
                              className="absolute top-1.5 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] hover:bg-[var(--tir-burgundy)]/5"
                              title="Remove this rest slot"
                              aria-label="Remove rest slot"
                            >
                              <X size={14} />
                            </button>
                          )}
                          </div>
                          {/* Inline athlete-log preview — visible WITHOUT
                              needing to expand. Shows feeling-smiley + RPE
                              + truncated note + quick-reply input so the
                              coach can scan the day at a glance. */}
                          {log && !isRest && (
                            <div
                              className="px-3 pb-2 pt-1.5 border-t border-dashed border-[var(--tir-border)]"
                              style={palette ? { backgroundColor: palette.bg } : undefined}
                              data-testid={`coach-grid-log-inline-${wo.id}`}
                            >
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-mono-tir text-[var(--tir-muted)]">
                                {log.feeling && (
                                  <FeelingChip
                                    value={log.feeling}
                                    testid={`coach-grid-feeling-inline-${wo.id}`}
                                  />
                                )}
                                {log.rpe_actual != null && (
                                  <span className="inline-flex items-center gap-1">
                                    <Star size={10} className="text-[var(--tir-burgundy)]" />
                                    RPE {log.rpe_actual}
                                  </span>
                                )}
                                {log.distance_actual_km != null && log.distance_actual_km > 0 && (
                                  <span>{Number(log.distance_actual_km).toFixed(1)} km</span>
                                )}
                                {log.duration_actual_min != null && log.duration_actual_min > 0 && (
                                  <span>{Math.round(log.duration_actual_min)} min</span>
                                )}
                                {log.source === "strava_auto" && (
                                  <span className="text-[#fc4c02] font-semibold">✦ Strava</span>
                                )}
                              </div>
                              {log.athlete_note && (
                                <div
                                  className="mt-1 text-[11px] text-[var(--tir-ink)] italic leading-snug bg-white border-l-2 border-[var(--tir-burgundy)] px-2 py-1 flex items-start gap-1.5 rounded-r-sm"
                                  data-testid={`coach-grid-note-inline-${wo.id}`}
                                >
                                  <MessageCircle size={10} className="text-[var(--tir-burgundy)] shrink-0 mt-0.5" />
                                  <span className="line-clamp-3">"{log.athlete_note}"</span>
                                </div>
                              )}
                              <div className="mt-1"><LogPhotoStrip log={log} size="sm" testid={`coach-athleteview-photos-${wo.id}`} /></div>
                              <InlineCoachReply
                                athleteId={athleteId}
                                programId={programId}
                                weekNumber={week.week_number}
                                dayLabel={day.label}
                                workoutId={wo.id}
                                testidSuffix={`grid-${wo.id}`}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </Draggable>
                    {/* Expanded panel — full workout structure + coach extras */}
                    {isExpanded && !isRest && (
                      <div
                        className="px-4 py-3 bg-[var(--tir-ivory)] text-[12px] space-y-3 ml-2"
                        data-testid={`coach-grid-expanded-${wo.id}`}
                      >
                        {/* Full workout structure (same component the athlete sees) */}
                        <WorkoutDetail workout={wo} />

                        {/* Athlete's log details (note + RPE + photo + Strava badge) */}
                        {log && (
                          <div
                            className="border-t border-dashed border-[var(--tir-border)] pt-2 space-y-1.5"
                            data-testid={`coach-grid-log-${wo.id}`}
                          >
                            <div className="text-[9px] tracking-[0.22em] uppercase font-bold text-[var(--tir-burgundy)]">
                              Athlete's log
                            </div>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-mono-tir text-[var(--tir-ink)]">
                              <span className="capitalize font-semibold">{log.status}</span>
                              {log.distance_actual_km != null && log.distance_actual_km > 0 && (
                                <span>{Number(log.distance_actual_km).toFixed(1)} km</span>
                              )}
                              {log.duration_actual_min != null && log.duration_actual_min > 0 && (
                                <span>{Math.round(log.duration_actual_min)} min</span>
                              )}
                              {log.rpe_actual != null && (
                                <span className="inline-flex items-center gap-1 text-[var(--tir-muted)]">
                                  <Star size={11} className="text-[var(--tir-burgundy)]" /> RPE {log.rpe_actual}
                                </span>
                              )}
                              {log.feeling && (
                                <span
                                  className="inline-flex items-center gap-1 text-[var(--tir-muted)] capitalize"
                                  title="How the athlete felt"
                                  data-testid={`coach-grid-feeling-${wo.id}`}
                                >
                                  · {String(log.feeling).replace(/_/g, " ")}
                                </span>
                              )}
                              {log.source === "strava_auto" && (
                                <span className="text-[#fc4c02] font-semibold">✦ Strava</span>
                              )}
                            </div>
                            {log.athlete_note && (
                              <div className="text-[12px] text-[var(--tir-ink)] italic leading-snug bg-white border-l-2 border-[var(--tir-burgundy)] px-2 py-1.5 flex items-start gap-1.5">
                                <MessageCircle size={11} className="text-[var(--tir-burgundy)] shrink-0 mt-0.5" />
                                <span>"{log.athlete_note}"</span>
                              </div>
                            )}
                            <LogPhotoStrip log={log} size="sm" testid={`coach-grid-photo-${wo.id}`} />
                          </div>
                        )}

                        {/* Coach action buttons */}
                        <div className="flex flex-wrap gap-2 pt-1">
                          {athleteId && programId && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditLog(wo, log);
                              }}
                              data-testid={`coach-grid-edit-log-${wo.id}`}
                              className="inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy-dark)] border border-[var(--tir-burgundy)]/40 hover:border-[var(--tir-burgundy)] px-2.5 py-1.5 rounded-sm"
                              title="Override the athlete's log (mark complete, edit distance, add coach note)"
                            >
                              <Pencil size={11} /> {log ? "Edit log" : "Log on athlete's behalf"}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenEditor(day.id, wo);
                            }}
                            data-testid={`coach-grid-edit-workout-${wo.id}`}
                            className="inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-navy)] hover:text-[var(--tir-ink)] border border-[var(--tir-navy)]/40 hover:border-[var(--tir-navy)] px-2.5 py-1.5 rounded-sm"
                            title="Edit the workout plan (intervals, zones, notes)"
                          >
                            <Pencil size={11} /> Edit workout
                          </button>
                          {!wo.is_alternative && wo.type !== "rest" && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onToggleOptional(day.id, wo.id);
                              }}
                              data-testid={`coach-grid-toggle-optional-${wo.id}`}
                              className={`inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase font-semibold border px-2.5 py-1.5 rounded-sm ${
                                wo.is_optional
                                  ? "text-[var(--tir-navy)] border-[var(--tir-navy)] bg-[var(--tir-navy)]/5"
                                  : "text-[var(--tir-muted)] border-[var(--tir-border)] hover:border-[var(--tir-ink)] hover:text-[var(--tir-ink)]"
                              }`}
                              title={wo.is_optional ? "Currently optional — click to make required" : "Mark as optional / bonus workout"}
                            >
                              <Star size={11} /> {wo.is_optional ? "Optional" : "Mark optional"}
                            </button>
                          )}
                          {/* One-click rest-day removal — the header
                              expand button is disabled for Rest tiles so
                              this is the only way for the coach to delete
                              a library-inherited Rest slot. */}
                          {wo.type === "rest" && onRemoveWorkout && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm("Remove this rest slot?")) {
                                  onRemoveWorkout(day.id, wo.id);
                                }
                              }}
                              data-testid={`coach-grid-remove-rest-${wo.id}`}
                              className="inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] border border-[var(--tir-border)] hover:border-[var(--tir-burgundy)] px-2.5 py-1.5 rounded-sm"
                              title="Remove this rest slot from the day"
                            >
                              <X size={11} /> Remove rest
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
              {provided.placeholder}
              {/* Athlete-added workouts — read-only overlay, never saved
                  into the master template. Click opens the log modal. */}
              {(additionsForDay?.(day.label) || []).map((a) => {
                const aLog = logsLookup ? logsLookup[`${week.week_number}-${day.label}-${a.id}`] : null;
                const aWorkout = {
                  id: a.id, type: a.type || "cross", title: a.title,
                  duration_or_distance: a.duration_or_distance || "",
                  notes: a.notes || "", segments: [], added_by_athlete: true,
                };
                return (
                  <div
                    key={a.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => athleteId && programId && onEditLog(aWorkout, aLog)}
                    data-testid={`coach-grid-athlete-added-${a.id}`}
                    className={`flex items-center gap-3 px-3 py-2.5 border-t border-dashed border-[var(--tir-navy)]/50 bg-[var(--tir-navy)]/[0.04] ${
                      athleteId && programId ? "cursor-pointer hover:bg-[var(--tir-navy)]/[0.08]" : ""
                    }`}
                    title="Added by the athlete on their portal"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="inline-block text-[8px] tracking-[0.2em] uppercase font-bold text-white bg-[var(--tir-navy)] px-1.5 py-0.5 rounded-sm">
                          Added by athlete
                        </span>
                        <span className="text-[13px] font-semibold text-[var(--tir-ink)] truncate">{a.title}</span>
                      </div>
                      <div className="text-[10px] font-mono-tir text-[var(--tir-muted)] mt-0.5 truncate">
                        {[a.duration_or_distance, a.type, a.notes].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {aLog?.status === "complete" && <Check size={13} color="#2F855A" />}
                      {aLog?.status === "partial" && <AlertTriangle size={13} color="#D69E2E" />}
                      {aLog?.status === "skipped" && <X size={13} color="#7A1F2B" />}
                    </div>
                  </div>
                );
              })}
              {/* + Workout button at the bottom of each day */}
              <button
                type="button"
                onClick={onAddWorkout}
                data-testid={`coach-grid-add-workout-${day.label}`}
                className="w-full text-[10px] tracking-[0.22em] uppercase py-2 inline-flex items-center justify-center gap-1 text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] hover:bg-[var(--tir-burgundy)]/5 border-t border-dashed border-[var(--tir-border)]"
              >
                <Plus size={12} /> Workout
              </button>
              {/* Strava activity readout if matched */}
              {dayComp && dayComp.activities && dayComp.activities.length > 0 && (
                <div className="px-3 py-1.5">
                  <DayActivityList dayCompletion={dayComp} />
                </div>
              )}
            </div>
          )}
        </Droppable>
      </div>
    </div>
  );
}


/**
 * CoachDayColumn — compact 7-across "Week row" view.
 *
 * Renders the same day as `CoachDayRow` but in a vertical column layout
 * so the entire week fits side-by-side on a laptop (M T W T F S S).
 * Workouts stack inside the column as compact colored tiles; the coach
 * clicks a tile to open the workout editor directly (no inline expand
 * — there's no horizontal room for it at this density, and the coach
 * is usually using this view for high-level shape rather than detail
 * editing).
 *
 * Drag-and-drop still works inside and across columns via the shared
 * `<DragDropContext>` in `WeekCanvas`.
 */
function CoachDayColumn({
  day, dayLabel, dayDate, isToday, race, dayComp, week, logsLookup,
  athleteId, programId, onOpenEditor, onAddWorkout, onEditLog,
  onDayNoteChange, onRemoveWorkout, additionsForDay, shiftForWorkout,
}) {
  const workouts = day.workouts || [];
  const allRest = workouts.length === 0 || workouts.every((w) => w.type === "rest");
  return (
    <div
      className={`bg-white rounded-md overflow-hidden border ${
        isToday ? "border-[var(--tir-navy)] border-2" : "border-[var(--tir-border)]"
      } ${allRest && !race ? "bg-white/60" : ""} flex flex-col`}
      data-testid={`coach-week-col-${day.label}`}
    >
      {/* Day pill header */}
      <div className="px-2 py-2 border-b border-[var(--tir-border)] bg-black/[0.03] flex items-baseline justify-between gap-1">
        <div className="font-serif-display text-lg uppercase text-[var(--tir-ink)] leading-none tracking-wide">
          {dayLabel}
        </div>
        <div className="flex items-center gap-1">
          {dayDate && <span className="text-[9px] font-mono-tir text-[var(--tir-muted)]">{dayDate}</span>}
          {dayComp?.status && <DayStatusDot status={dayComp.status} />}
        </div>
      </div>
      {/* Optional day-level coach note (slim) */}
      <DayNoteInput day={day} onChange={onDayNoteChange} />
      {/* Race chip — clickable for coach override-log */}
      {race && (() => {
        const raceWorkoutId = `race:${race.id}`;
        const raceKey = `${week.week_number}-${day.label}-${raceWorkoutId}`;
        let raceLog = logsLookup ? logsLookup[raceKey] : null;
        if (!raceLog && logsLookup) {
          const prefix = `${week.week_number}-${day.label}-`;
          for (const [k, v] of Object.entries(logsLookup)) {
            if (k.startsWith(prefix) && k !== raceKey && v) {
              raceLog = v;
              break;
            }
          }
        }
        const status = raceLog?.status;
        const raceWorkout = {
          id: raceWorkoutId,
          type: "race",
          title: race.name || "Race",
          duration_or_distance: race.distance || "",
          is_race: true,
          race_priority: race.priority || null,
          session_note: race.notes || "",
        };
        return (
        <div
          role="button"
          tabIndex={0}
          onClick={() => athleteId && programId && onEditLog(raceWorkout, raceLog)}
          onKeyDown={(e) => {
            if (athleteId && programId && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              onEditLog(raceWorkout, raceLog);
            }
          }}
          className={`mx-1.5 mt-1.5 border border-[var(--tir-burgundy)] bg-[#fdf6e7] px-1.5 py-1 leading-tight rounded-sm ${
            athleteId && programId ? "cursor-pointer hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--tir-burgundy)]/40" : ""
          }`}
          data-testid={`coach-week-col-race-${day.label}`}
          aria-label={`${race.name} — click to log on athlete's behalf`}
        >
          <div className="flex items-center gap-1 text-[8px] tracking-[0.18em] uppercase font-bold text-[var(--tir-burgundy)]">
            <Trophy size={9} className="shrink-0" />
            <span className="truncate flex-1">{race.priority ? `Race · ${race.priority}` : "Race"}</span>
            {status === "complete" && <Check size={9} color="#2F855A" className="shrink-0" />}
            {status === "partial" && <AlertTriangle size={9} color="#D69E2E" className="shrink-0" />}
            {status === "skipped" && <X size={9} color="#7A1F2B" className="shrink-0" />}
          </div>
          <div className="text-[10px] font-semibold text-[var(--tir-ink)] break-words leading-snug">
            {race.name}
          </div>
          {race.distance && !raceLog && (
            <div className="text-[9px] font-mono-tir text-[var(--tir-muted)]">{race.distance}</div>
          )}
          {raceLog && raceLog.distance_actual_km > 0 && (
            <div className="text-[9px] font-mono-tir text-[var(--tir-muted)]">
              {Number(raceLog.distance_actual_km).toFixed(1)} km
              {raceLog.duration_actual_min > 0 && ` · ${Math.round(raceLog.duration_actual_min)} min`}
            </div>
          )}
        </div>
        );
      })()}
      {/* Workout tiles — drag-droppable inside this day's droppable area */}
      <Droppable droppableId={day.id}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`flex-1 p-1.5 space-y-1.5 min-h-[80px] ${
              snapshot.isDraggingOver ? "bg-[var(--tir-burgundy)]/5" : ""
            }`}
          >
            {workouts.map((wo, woIdx) => {
              const log = logsLookup ? logsLookup[`${week.week_number}-${day.label}-${wo.id}`] : null;
              const isRest = wo.type === "rest";
              const accent = getWorkoutAccent(wo.type);
              const Icon = getWorkoutIcon(wo.type);
              const status = log?.status;
              const palette = status === "complete"
                ? { bg: "#F0FDF4", border: "#86EFAC" }
                : status === "partial"
                  ? { bg: "#FEFCE8", border: "#FDE68A" }
                  : status === "skipped"
                    ? { bg: "#FDF2F2", border: "#FCA5A5" }
                    : null;
              const typeLabel = WORKOUT_TYPE_MAP[wo.type]?.label || wo.type;
              return (
                <React.Fragment key={wo.id}>
                  {woIdx > 0 && wo.is_alternative && (
                    <div className="flex items-center gap-1 text-[8px] tracking-[0.22em] uppercase font-bold text-[var(--tir-burgundy)]">
                      <div className="flex-1 h-px bg-[var(--tir-border)]" />
                      OR
                      <div className="flex-1 h-px bg-[var(--tir-border)]" />
                    </div>
                  )}
                  <Draggable draggableId={wo.id} index={woIdx} isDragDisabled={isRest}>
                    {(dragProvided, dragSnapshot) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        data-testid={`coach-week-col-workout-${wo.id}`}
                        className={`relative flex items-stretch w-full rounded-sm border overflow-hidden ${
                          isRest ? "opacity-60" : ""
                        } ${dragSnapshot.isDragging ? "shadow-lg bg-white" : ""}`}
                        style={{
                          backgroundColor: palette ? palette.bg : "white",
                          borderColor: palette ? palette.border : "var(--tir-border)",
                        }}
                      >
                        {/* Compact "×" delete on Rest tiles so the coach
                            can remove a library-inherited rest slot
                            without needing to open the editor. Rest
                            tiles are click-disabled everywhere else. */}
                        {isRest && onRemoveWorkout && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm("Remove this rest slot?")) {
                                onRemoveWorkout(day.id, wo.id);
                              }
                            }}
                            data-testid={`coach-week-col-remove-rest-${wo.id}`}
                            className="absolute top-0.5 right-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] hover:bg-[var(--tir-burgundy)]/10 z-10"
                            title="Remove this rest slot"
                            aria-label="Remove rest slot"
                          >
                            <X size={11} />
                          </button>
                        )}
                        {/* Left accent stripe + drag handle. Lives as a
                            flex SIBLING (not absolute-positioned) so it's
                            not covered in z-order by the content div —
                            that was the silent-fail in Week-view DnD.
                            Widened to w-6 so it's actually grabbable on
                            the narrow week-view columns (was w-3.5, only
                            14px wide — nearly impossible to hit). */}
                        <div
                          {...(isRest ? {} : dragProvided.dragHandleProps)}
                          aria-label={isRest ? undefined : "Drag workout"}
                          data-testid={`coach-week-col-drag-${wo.id}`}
                          className={`w-6 shrink-0 self-stretch flex items-center justify-center ${isRest ? "" : "cursor-grab active:cursor-grabbing hover:brightness-110"}`}
                          style={{ backgroundColor: accent }}
                          onClick={(e) => e.stopPropagation()}
                          title={isRest ? "" : "Drag to reschedule"}
                        >
                          {!isRest && (
                            <GripVertical
                              size={14}
                              className="text-white/90"
                              strokeWidth={2.4}
                              aria-hidden="true"
                            />
                          )}
                        </div>
                        {/* Clickable content — opens the editor / log
                            modal. Sibling of the drag handle so clicks
                            don't race with drag-sensor mouseDown. */}
                        <div
                          role={isRest ? undefined : "button"}
                          tabIndex={isRest ? -1 : 0}
                          onClick={() => {
                            if (isRest) return;
                            // Logged by the athlete → show their log first (the
                            // log view has an "Edit workout plan" link).
                            if (log && athleteId && programId) onEditLog(wo, log);
                            else onOpenEditor(day.id, wo);
                          }}
                          onKeyDown={(e) => {
                            if (isRest) return;
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              if (log && athleteId && programId) onEditLog(wo, log);
                              else onOpenEditor(day.id, wo);
                            }
                          }}
                          aria-disabled={isRest || undefined}
                          className={`flex-1 min-w-0 pl-1.5 pr-1.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-[var(--tir-burgundy)]/40 ${
                            isRest ? "cursor-default" : "cursor-pointer hover:bg-black/[0.02]"
                          }`}
                        >
                          <div className="flex items-center gap-1.5">
                            <Icon size={11} style={{ color: accent }} strokeWidth={2.4} className="shrink-0" />
                            {wo.is_optional && (
                              <span className="text-[7px] tracking-[0.2em] uppercase font-bold text-[var(--tir-navy)] border border-[var(--tir-navy)]/40 px-1 rounded-sm leading-none py-0.5">
                                Opt
                              </span>
                            )}
                            <span className="text-[11px] font-semibold text-[var(--tir-ink)] leading-tight truncate flex-1">
                              {wo.title || typeLabel}
                            </span>
                            {(() => {
                              const s = shiftForWorkout?.(wo.id);
                              return s ? (
                                <span
                                  className="text-[7px] tracking-[0.14em] uppercase font-bold text-white bg-[var(--tir-navy)] px-1 py-0.5 rounded-sm shrink-0"
                                  data-testid={`coach-col-shift-badge-${wo.id}`}
                                  title="The athlete moved this workout on their portal"
                                >
                                  →{s.to_day}
                                </span>
                              ) : null;
                            })()}
                            {status === "complete" && <Check size={11} color="#2F855A" className="shrink-0" />}
                            {status === "partial" && <AlertTriangle size={11} color="#D69E2E" className="shrink-0" />}
                            {status === "skipped" && <X size={11} color="#7A1F2B" className="shrink-0" />}
                          </div>
                          {wo.duration_or_distance && (
                            <div className="text-[9px] font-mono-tir text-[var(--tir-muted)] tracking-wide mt-0.5 truncate">
                              {wo.duration_or_distance}
                            </div>
                          )}
                          {/* Athlete log preview — feeling smiley + RPE
                              + truncated note + Edit-log + quick reply,
                              all visible inline so the coach doesn't have
                              to open every workout to scan how the week
                              went. */}
                          {log && !isRest && (
                            <div className="mt-1 space-y-1" data-testid={`coach-week-col-log-${wo.id}`}>
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[9px] font-mono-tir text-[var(--tir-muted)]">
                                {log.feeling && (
                                  <FeelingChip
                                    value={log.feeling}
                                    compact
                                    testid={`coach-week-col-feeling-${wo.id}`}
                                  />
                                )}
                                {log.rpe_actual != null && (
                                  <span className="inline-flex items-center gap-0.5">
                                    <Star size={8} className="text-[var(--tir-burgundy)]" /> {log.rpe_actual}
                                  </span>
                                )}
                                {log.distance_actual_km != null && log.distance_actual_km > 0 && (
                                  <span>{Number(log.distance_actual_km).toFixed(1)}k</span>
                                )}
                                {log.source === "strava_auto" && (
                                  <span className="text-[#fc4c02] font-semibold">✦</span>
                                )}
                              </div>
                              {log.athlete_note && (
                                <div
                                  className="text-[10px] text-[var(--tir-ink)] italic leading-snug bg-white/70 border-l-2 border-[var(--tir-burgundy)] px-1.5 py-1 line-clamp-2 rounded-r-sm"
                                  data-testid={`coach-week-col-note-${wo.id}`}
                                  title={log.athlete_note}
                                >
                                  "{log.athlete_note}"
                                </div>
                              )}
                              <LogPhotoStrip log={log} size="xs" testid={`coach-week-col-photos-${wo.id}`} />
                              {athleteId && programId && (
                                <InlineCoachReply
                                  athleteId={athleteId}
                                  programId={programId}
                                  weekNumber={week.week_number}
                                  dayLabel={day.label}
                                  workoutId={wo.id}
                                  testidSuffix={`week-${wo.id}`}
                                />
                              )}
                            </div>
                          )}
                          {log && athleteId && programId && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditLog(wo, log);
                              }}
                              data-testid={`coach-week-col-edit-log-${wo.id}`}
                              className="mt-1 inline-flex items-center gap-0.5 text-[8px] tracking-[0.18em] uppercase font-semibold text-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy-dark)]"
                              title="Edit / override the athlete's log"
                            >
                              <Pencil size={8} /> Edit log
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </Draggable>
                </React.Fragment>
              );
            })}
            {provided.placeholder}
            {/* Athlete-added workouts — read-only overlay, never saved into
                the master template. Click opens the log modal. */}
            {(additionsForDay?.(day.label) || []).map((a) => {
              const aLog = logsLookup ? logsLookup[`${week.week_number}-${day.label}-${a.id}`] : null;
              const aWorkout = {
                id: a.id, type: a.type || "cross", title: a.title,
                duration_or_distance: a.duration_or_distance || "",
                notes: a.notes || "", segments: [], added_by_athlete: true,
              };
              return (
                <div
                  key={a.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => athleteId && programId && onEditLog(aWorkout, aLog)}
                  data-testid={`coach-week-col-athlete-added-${a.id}`}
                  className={`w-full rounded-sm border border-dashed border-[var(--tir-navy)]/60 bg-[var(--tir-navy)]/[0.05] px-1.5 py-1.5 ${
                    athleteId && programId ? "cursor-pointer hover:shadow-sm" : ""
                  }`}
                  title="Added by the athlete on their portal"
                >
                  <div className="text-[7px] tracking-[0.16em] uppercase font-bold text-[var(--tir-navy)] mb-0.5">
                    Added by athlete
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-[11px] font-semibold text-[var(--tir-ink)] leading-tight truncate flex-1">
                      {a.title}
                    </span>
                    {aLog?.status === "complete" && <Check size={11} color="#2F855A" className="shrink-0" />}
                    {aLog?.status === "partial" && <AlertTriangle size={11} color="#D69E2E" className="shrink-0" />}
                    {aLog?.status === "skipped" && <X size={11} color="#7A1F2B" className="shrink-0" />}
                  </div>
                  {(a.duration_or_distance || a.notes) && (
                    <div className="text-[9px] font-mono-tir text-[var(--tir-muted)] mt-0.5 truncate">
                      {a.duration_or_distance || a.notes}
                    </div>
                  )}
                </div>
              );
            })}
            {/* Strava activity readout */}
            {dayComp && dayComp.activities && dayComp.activities.length > 0 && (
              <div className="text-[9px]">
                <DayActivityList dayCompletion={dayComp} />
              </div>
            )}
          </div>
        )}
      </Droppable>
      {/* Add workout button */}
      <button
        type="button"
        onClick={onAddWorkout}
        data-testid={`coach-week-col-add-workout-${day.label}`}
        className="w-full text-[9px] tracking-[0.22em] uppercase py-1.5 inline-flex items-center justify-center gap-1 text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] hover:bg-[var(--tir-burgundy)]/5 border-t border-dashed border-[var(--tir-border)]"
      >
        <Plus size={10} /> Workout
      </button>
    </div>
  );
}
